import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { message, Modal } from 'antd';
import Layout from 'Layout';
import { FOLDER_COLORS, LibraryFile, FolderItem, Visibility } from 'mock/folder.data';
import FolderCard from './components/FolderCard';
import FileCard from './components/FileCard';
import ViewToggle, { ViewMode } from './components/ViewToggle';
import Breadcrumb, { BreadcrumbItem } from './components/Breadcrumb';
import NewFolderModal from './components/NewFolderModal';
import MoveModal from './components/MoveModal';
import UploadModal from './components/UploadModal';
import RenameModal from './components/RenameModal';
import ShareModal from './components/ShareModal';
import LibraryTabs from './components/LibraryTabs';
import Pagination from './components/Pagination';
import { createFolder, deleteFile, deleteFolder, downloadFile, getLibraryContent, updateFolder, updateFile, openFile } from './function/api';
import { mapFolderToFolderItem, mapLibraryContentResponse } from './function/converter';
import { useAuth } from 'hooks/useAuth';
import './styles.scss';

type FileTypeFilter = 'all' | LibraryFile['type'];

const FILE_TYPE_OPTIONS: { value: FileTypeFilter; label: string }[] = [
  { value: 'all', label: 'Tất cả loại' },
  { value: 'PDF', label: 'PDF' },
  { value: 'DOCX', label: 'Word' },
  { value: 'XLSX', label: 'Excel' },
  { value: 'PPTX', label: 'PowerPoint' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'IMAGE', label: 'Ảnh' },
];

const PAGE_SIZE = 10;

function buildBreadcrumbs(folderId: string | null, allFolders: FolderItem[]): BreadcrumbItem[] {
  const root: BreadcrumbItem = { id: null, name: 'Thư viện của tôi' };
  if (!folderId) return [root];
  const trail: BreadcrumbItem[] = [];
  let node = allFolders.find((f) => f.id === folderId);
  while (node) {
    trail.unshift({ id: node.id, name: node.name });
    const parentId = node.parentId;
    node = parentId ? allFolders.find((f) => f.id === parentId) : undefined;
  }

  if (trail.length === 0 || trail[trail.length - 1].id !== folderId) {
    trail.push({ id: folderId, name: `Thư mục #${folderId}` });
  }

  return [root, ...trail];
}

function getDescendantIds(folderId: string, allFolders: FolderItem[]): string[] {
  const children = allFolders.filter((f) => f.parentId === folderId).map((f) => f.id);
  return children.flatMap((id) => [id, ...getDescendantIds(id, allFolders)]);
}

interface MoveTarget {
  id: string;
  name: string;
  type: 'file' | 'folder';
  currentFolderId: string | null;
}

interface ShareTarget {
  id: string;
  name: string;
  visible: Visibility;
  type: 'file' | 'folder';
}

const LibraryPage: React.FC = () => {
  const { folderId = null } = useParams<{ folderId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [messageApi, messageContextHolder] = message.useMessage();
  const { user } = useAuth();
  const isAdmin = !!(user?.roles && (user.roles.some((role) => role.name === 'admin' || role.name?.includes('admin'))));

  const tab = searchParams.get('tab') ?? 'mine';
  const isSystemTab = tab === 'system';
  const currentFolderId = folderId ?? null;
  const isPublicFilter = isSystemTab;

  const numericFolderId = useMemo(() => {
    if (!folderId) {
      return null;
    }

    const parsedFolderId = Number(folderId);
    return Number.isNaN(parsedFolderId) ? null : parsedFolderId;
  }, [folderId]);

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<FileTypeFilter>('all');
  const [isNewFolderOpen, setIsNewFolderOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string; type: 'folder' | 'file' } | null>(null);
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);
  const [page, setPage] = useState(1);
  const [currentFolders, setCurrentFolders] = useState<FolderItem[]>([]);
  const [currentFiles, setCurrentFiles] = useState<LibraryFile[]>([]);
  const [folderLookup, setFolderLookup] = useState<Record<string, FolderItem>>({});
  const [activeFile, setActiveFile] = useState<LibraryFile | null>(null);
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<FolderItem | null>(null);
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const allFolderItems = useMemo(() => Object.values(folderLookup), [folderLookup]);

  useEffect(() => {
    setPage(1);
    setSearch('');
    setTypeFilter('all');
  }, [folderId, tab]);

  useEffect(() => {
    setFolderLookup({});
  }, [tab]);

  useEffect(() => { setPage(1); }, [search, typeFilter]);

  const breadcrumbs = useMemo(
    () => buildBreadcrumbs(folderId ?? null, allFolderItems),
    [folderId, allFolderItems]
  );

  useEffect(() => {
    let isDisposed = false;

    const loadLibraryContent = async () => {
      if (folderId && numericFolderId === null) {
        setCurrentFolders([]);
        setCurrentFiles([]);
        setErrorMessage('Thư mục không hợp lệ.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await getLibraryContent({
          parent_id: numericFolderId,
          is_public: isPublicFilter,
        });

        if (!response.status) {
          throw new Error('Không thể tải nội dung thư viện.');
        }

        const mappedContent = mapLibraryContentResponse(response);

        if (isDisposed) {
          return;
        }

        setCurrentFolders(mappedContent.folders);
        setCurrentFiles(mappedContent.files);
        setFolderLookup((prev) => {
          const next = { ...prev };
          mappedContent.folders.forEach((folder) => {
            next[folder.id] = folder;
          });
          return next;
        });
      } catch (error) {
        if (isDisposed) {
          return;
        }

        setCurrentFolders([]);
        setCurrentFiles([]);
        setErrorMessage(error instanceof Error ? error.message : 'Không thể tải nội dung thư viện.');
      } finally {
        if (!isDisposed) {
          setIsLoading(false);
        }
      }
    };

    loadLibraryContent();

    return () => {
      isDisposed = true;
    };
  }, [folderId, numericFolderId, isPublicFilter]);

  const folders = useMemo<FolderItem[]>(() => {
    let list = [...currentFolders];

    if (!search.trim()) return list;
    return list.filter((f) => f.name.toLowerCase().includes(search.toLowerCase()));
  }, [currentFolders, search]);

  const allFiles = useMemo<LibraryFile[]>(() => {
    let list = [...currentFiles];

    if (search.trim()) list = list.filter((f) => f.name.toLowerCase().includes(search.toLowerCase()));
    if (typeFilter !== 'all') list = list.filter((f) => f.type === typeFilter);
    return list;
  }, [currentFiles, search, typeFilter]);

  const pagedFiles = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return allFiles.slice(start, start + PAGE_SIZE);
  }, [allFiles, page]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const switchTab = (key: string) => navigate(`/library?tab=${key}`);
  const openFolder = (id: string) => navigate(`/library/${id}?tab=${tab}`);

  const handleNewFolder = async (name: string, colorId: string, visible: Visibility) => {
    try {
      const colorHex = FOLDER_COLORS[colorId]?.startColor ?? FOLDER_COLORS['3'].startColor;

      const response = await createFolder({
        name,
        color: colorHex,
        is_public: visible === 'public',
        parent_id: numericFolderId,
      });

      if (!response.status) {
        throw new Error(response.message || 'Không thể tạo thư mục mới.');
      }

      const newFolder = mapFolderToFolderItem(response.data);
      setCurrentFolders((prev) => [...prev, newFolder]);
      setFolderLookup((prev) => ({
        ...prev,
        [newFolder.id]: newFolder,
      }));
      messageApi.success('Tạo thư mục thành công.');
    } catch (error) {
      const nextErrorMessage = error instanceof Error ? error.message : 'Không thể tạo thư mục mới.';
      messageApi.error(nextErrorMessage);
      throw error;
    }
  };

  const handleDeleteFolder = (folder: FolderItem) => {
    setFolderToDelete(folder);
  };

  const handleOpenFile = async (file: LibraryFile) => {
    try {
      if (file.type === 'PDF' || file.name.toLowerCase().endsWith('.pdf')) {
        navigate(`/library/view/${file.id}`);
        return;
      }
      await openFile(file.id);
    } catch (error) {
      const nextErrorMessage = error instanceof Error ? error.message : 'Không thể mở tệp.';
      await downloadFile(file.id);
      messageApi.error(nextErrorMessage);
    }
  };

  const handleDownloadFile = async (file: LibraryFile) => {
    try {
      await downloadFile(file.id);
    } catch (error) {
      const nextErrorMessage = error instanceof Error ? error.message : 'Không thể tải xuống tệp.';
      messageApi.error(nextErrorMessage);
    }
  };

  const handleDeleteFile = (file: LibraryFile) => {
    Modal.confirm({
      title: 'Xóa tệp',
      content: `Bạn có chắc muốn xóa tệp "${file.name}" không?`,
      okText: 'Xóa',
      cancelText: 'Hủy',
      okButtonProps: { danger: true },
      onOk: async () => {
        const numericFileId = Number(file.id);

        if (Number.isNaN(numericFileId)) {
          messageApi.error('ID tệp không hợp lệ.');
          return;
        }

        try {
          await deleteFile(numericFileId);
          setCurrentFiles((prev) => prev.filter((item) => item.id !== file.id));
          messageApi.success('Xóa tệp thành công.');
        } catch (error) {
          const nextErrorMessage = error instanceof Error ? error.message : 'Không thể xóa tệp.';
          messageApi.error(nextErrorMessage);
          throw error;
        }
      },
    });
  };

  const confirmDeleteFolder = async () => {
    if (!folderToDelete) {
      return;
    }

    const targetFolder = folderToDelete;
    const folderId = Number(targetFolder.id);
    if (Number.isNaN(folderId)) {
      setFolderToDelete(null);
      messageApi.error('ID thư mục không hợp lệ.');
      return;
    }

    try {
      setIsDeletingFolder(true);
      await deleteFolder(folderId);

      const descendantIds = new Set([targetFolder.id, ...getDescendantIds(targetFolder.id, allFolderItems)]);

      setCurrentFolders((prev) => prev.filter((item) => !descendantIds.has(item.id)));
      setFolderLookup((prev) => {
        const next = { ...prev };
        descendantIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
      setFolderToDelete(null);
      messageApi.success('Xóa thư mục thành công.');
    } catch (error) {
      const nextErrorMessage = error instanceof Error ? error.message : 'Không thể xóa thư mục.';
      messageApi.error(nextErrorMessage);
    } finally {
      setIsDeletingFolder(false);
    }
  };

  const handleConfirmRename = async (newName: string) => {
    if (!renameTarget) return;
    const numericId = Number(renameTarget.id);
    if (Number.isNaN(numericId)) {
      messageApi.error('ID không hợp lệ.');
      return;
    }
    try {
      if (renameTarget.type === 'folder') {
        await updateFolder(numericId, { name: newName });
        setCurrentFolders((prev) => prev.map((f) => f.id === renameTarget.id ? { ...f, name: newName } : f));
        setFolderLookup((prev) => {
          const folder = prev[renameTarget.id];
          if (!folder) return prev;
          return { ...prev, [renameTarget.id]: { ...folder, name: newName } };
        });
      } else {
        await updateFile(numericId, newName, null);
        setCurrentFiles((prev) => prev.map((f) => f.id === renameTarget.id ? { ...f, name: newName } : f));
      }
      messageApi.success('Đã đổi tên thành công.');
      setRenameTarget(null);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Không thể đổi tên.';
      messageApi.error(msg);
    }
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveFile(currentFiles.find((f) => f.id === active.id) ?? null);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveFile(null);
    if (!over) return;
    const fileId = active.id as string;
    const targetFolderId = over.id as string;

    setCurrentFiles((prev) => (
      prev
        .map((file) => (file.id === fileId ? { ...file, folderId: targetFolderId } : file))
        .filter((file) => file.folderId === currentFolderId)
    ));
  };

  const handleMove = async (targetFolderId: string | null) => {
    if (!moveTarget) return;
    const numericId = Number(moveTarget.id);
    if (Number.isNaN(numericId)) {
      messageApi.error('ID không hợp lệ.');
      return;
    }
    const numericTargetId = targetFolderId !== null ? Number(targetFolderId) : null;
    try {
      if (moveTarget.type === 'file') {
        await updateFile(numericId, moveTarget.name, numericTargetId);
        setCurrentFiles((prev) => {
          if (targetFolderId === currentFolderId) {
            return prev.map((file) => (file.id === moveTarget.id ? { ...file, folderId: targetFolderId } : file));
          }
          return prev.filter((file) => file.id !== moveTarget.id);
        });
      } else {
        await updateFolder(numericId, { parent_id: numericTargetId });
        setCurrentFolders((prev) => {
          if (targetFolderId === currentFolderId) {
            return prev.map((folder) => (folder.id === moveTarget.id ? { ...folder, parentId: targetFolderId } : folder));
          }
          return prev.filter((folder) => folder.id !== moveTarget.id);
        });
        setFolderLookup((prev) => {
          const folder = prev[moveTarget.id];
          if (!folder) return prev;
          return {
            ...prev,
            [moveTarget.id]: {
              ...folder,
              parentId: targetFolderId,
            },
          };
        });
      }
      messageApi.success('Di chuyển thành công.');
      setMoveTarget(null);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Không thể di chuyển.';
      messageApi.error(msg);
    }
  };

  const openMoveFile = (file: LibraryFile) =>
    setMoveTarget({ id: file.id, name: file.name, type: 'file', currentFolderId: file.folderId });

  const openMoveFolder = (folder: FolderItem) =>
    setMoveTarget({ id: folder.id, name: folder.name, type: 'folder', currentFolderId: folder.parentId });

  const moveExcludeIds = useMemo(() => {
    if (!moveTarget || moveTarget.type !== 'folder') return [];
    return [moveTarget.id, ...getDescendantIds(moveTarget.id, allFolderItems)];
  }, [moveTarget, allFolderItems]);

  const folderDepth = breadcrumbs.length - 1;
  const isEmpty = folders.length === 0 && allFiles.length === 0;

  return (
    <Layout>
      {messageContextHolder}
      <Helmet>
        <title>Thư viện | LUMIR</title>
      </Helmet>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="library">
          <div className="library__toolbar">
            <div className="library__toolbar-left">
              <h1 className="library__title">Thư viện</h1>
              <Breadcrumb items={breadcrumbs} />
            </div>
            <div className="library__toolbar-right">
              <div className="library__search">
                <svg className="library__search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  className="library__search-input"
                  type="text"
                  placeholder="Tìm kiếm..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <select
                className="library__type-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as FileTypeFilter)}
              >
                {FILE_TYPE_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <ViewToggle view={viewMode} onChange={setViewMode} />
            </div>
          </div>

          <div className="library__top-bar">
            <LibraryTabs active={tab} onChange={switchTab} />
            {!isSystemTab && (
              <div className="library__actions">
                <button
                  className="library__action-btn library__action-btn--primary"
                  onClick={() => setIsNewFolderOpen(true)}
                  disabled={folderDepth >= 3}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    <line x1="12" y1="11" x2="12" y2="17" /><line x1="9" y1="14" x2="15" y2="14" />
                  </svg>
                  Thư mục mới
                </button>
                <button className="library__action-btn" onClick={() => setIsUploadOpen(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Tải lên
                </button>
              </div>
            )}
          </div>

          {viewMode === 'list' && !isEmpty && (
            <div className="lib-list-header">
              <span />
              <span>Tên</span>
              <span>Chủ nhân</span>
              <span>Kích thước</span>
              <span>Ngày sửa</span>
              <span />
            </div>
          )}

          {isLoading ? (
            <div className="library__status">
              <div className="library__spinner" />
              <p>Đang tải nội dung thư viện...</p>
            </div>
          ) : errorMessage ? (
            <div className="library__status library__status--error">
              <p>{errorMessage}</p>
            </div>
          ) : isEmpty ? (
            <div className="library__empty">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
              <p>Không có nội dung nào</p>
            </div>
          ) : (
            <>
              {folders.length > 0 && (
                <section className="library__section">
                  <p className="library__section-label">Thư mục</p>
                  {viewMode === 'grid' ? (
                    <div className="library__folder-grid">
                      {folders.map((f) => (
                        <FolderCard
                          key={f.id}
                          folder={f}
                          variant="grid"
                          onClick={() => openFolder(f.id)}
                          isAdmin={isAdmin}
                          {...(!(isSystemTab && !isAdmin) && {
                            onMove: () => openMoveFolder(f),
                            onDelete: () => handleDeleteFolder(f),
                            onRename: () => setRenameTarget({ id: f.id, name: f.name, type: 'folder' }),
                            onShare: () => setShareTarget({ id: f.id, name: f.name, visible: f.visible, type: 'folder' }),
                          })}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="library__list">
                      {folders.map((f) => (
                        <FolderCard
                          key={f.id}
                          folder={f}
                          variant="list"
                          onClick={() => openFolder(f.id)}
                          isAdmin={isAdmin}
                          {...(!(isSystemTab && !isAdmin) && {
                            onMove: () => openMoveFolder(f),
                            onDelete: () => handleDeleteFolder(f),
                            onRename: () => setRenameTarget({ id: f.id, name: f.name, type: 'folder' }),
                            onShare: () => setShareTarget({ id: f.id, name: f.name, visible: f.visible, type: 'folder' }),
                          })}
                        />
                      ))}
                    </div>
                  )}
                </section>
              )}

              {allFiles.length > 0 && (
                <section className="library__section">
                  <p className="library__section-label">Tệp</p>
                  {viewMode === 'grid' ? (
                    <div className="library__file-grid">
                      {pagedFiles.map((f) => (
                        <FileCard
                          key={f.id}
                          file={f}
                          variant="grid"
                          onDownload={() => handleDownloadFile(f)}
                          isAdmin={isAdmin}
                          {...(!(isSystemTab && !isAdmin) && {
                            onMove: () => openMoveFile(f),
                            onOpen: () => handleOpenFile(f),
                            onRename: () => setRenameTarget({ id: f.id, name: f.name, type: 'file' }),
                            onShare: () => setShareTarget({ id: f.id, name: f.name, visible: f.visible, type: 'file' }),
                            onDelete: () => handleDeleteFile(f),
                          })}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="library__list">
                      {pagedFiles.map((f) => (
                        <FileCard
                          key={f.id}
                          file={f}
                          variant="list"
                          onDownload={() => handleDownloadFile(f)}
                          isAdmin={isAdmin}
                          {...(!(isSystemTab && !isAdmin) && {
                            onMove: () => openMoveFile(f),
                            onOpen: () => handleOpenFile(f),
                            onRename: () => setRenameTarget({ id: f.id, name: f.name, type: 'file' }),
                            onShare: () => setShareTarget({ id: f.id, name: f.name, visible: f.visible, type: 'file' }),
                            onDelete: () => handleDeleteFile(f),
                          })}
                        />
                      ))}
                    </div>
                  )}
                  <Pagination page={page} total={allFiles.length} pageSize={PAGE_SIZE} onChange={setPage} />
                </section>
              )}
            </>
          )}
        </div>

        <DragOverlay>
          {activeFile && <FileCard file={activeFile} variant={viewMode} isDragOverlay />}
        </DragOverlay>
      </DndContext>

      <NewFolderModal
        open={isNewFolderOpen}
        onClose={() => setIsNewFolderOpen(false)}
        onReload={async () => {
          setIsLoading(true);
          try {
            const response = await getLibraryContent({
              parent_id: numericFolderId,
              is_public: isPublicFilter,
            });
            if (response.status) {
              const mappedContent = mapLibraryContentResponse(response);
              setCurrentFolders(mappedContent.folders);
              setCurrentFiles(mappedContent.files);
            }
          } catch {
            // handle silently
          } finally {
            setIsLoading(false);
          }
        }}
        onConfirm={handleNewFolder}
      />

      <MoveModal
        open={moveTarget !== null}
        onClose={() => setMoveTarget(null)}
        onMove={handleMove}
        itemName={moveTarget?.name ?? ''}
        currentFolderId={moveTarget?.currentFolderId ?? null}
        allFolders={allFolderItems}
        excludeIds={moveExcludeIds}
      />

      <UploadModal
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        folderId={numericFolderId}
        onUploadSuccess={async (file) => {
          setCurrentFiles((prev) => [...prev, file]);
          setIsLoading(true);
          try {
            const response = await getLibraryContent({
              parent_id: numericFolderId,
              is_public: isPublicFilter,
            });
            if (response.status) {
              const mappedContent = mapLibraryContentResponse(response);
              setCurrentFolders(mappedContent.folders);
              setCurrentFiles(mappedContent.files);
            }
          } catch {
            // handle silently
          } finally {
            setIsLoading(false);
          }
        }}
      />

      <RenameModal
        open={renameTarget !== null}
        onClose={() => setRenameTarget(null)}
        currentName={renameTarget?.name ?? ''}
        onConfirm={handleConfirmRename}
      />

      <ShareModal
        open={shareTarget !== null}
        onClose={() => setShareTarget(null)}
        itemId={shareTarget?.id ?? ''}
        itemName={shareTarget?.name ?? ''}
        itemVisible={shareTarget?.visible ?? 'private'}
        itemType={shareTarget?.type ?? 'file'}
      />

      <Modal
        open={folderToDelete !== null}
        title="Xóa thư mục"
        onCancel={() => !isDeletingFolder && setFolderToDelete(null)}
        onOk={confirmDeleteFolder}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true, loading: isDeletingFolder }}
        cancelButtonProps={{ disabled: isDeletingFolder }}
      >
        <p>
          Bạn có chắc muốn xóa thư mục "{folderToDelete?.name}" không?
        </p>
      </Modal>
    </Layout>
  );
};

export default LibraryPage;
